/** Type file for MDM (Master Data Management) - Customer, Supplier, Distributor entities. */
import type { Permission, ProductSelectorCSVDocument, UUID } from "./types";
import type {
  Assets,
  AttributeGroupSummary,
  AttributeTemplateSchema,
  CollectionSchema,
  ProductUploadStatus,
  TemplateTabData,
} from "./types.PIM";

export type EntityType = "customer" | "supplier" | "distributor";

export type EntityStatus =
  | "draft"
  | "published"
  | "staged"
  | "unpublished"
  | "unpublished_staged"
  | "archived";

export type EntityProfileSummary = {
  id: UUID;
  entity_number: string | null;
  tenant_id: UUID;
  entity_type: EntityType;
  name: string;
  status: EntityStatus;
  created_by: string | null;
  modified_by: string | null;
  created_at: string;
  modified_at: string;
  // Placeholder fields — populated once default attributes and hierarchy are implemented.
  erp_id: string | null;
  duns_number: string | null;
  parent_entity_name: string | null;
  subsidiaries_count: number | null;
  location: string | null;
  is_parent: boolean;
  is_subsidiary: boolean;
  attributes: { name: string; display_name: string; value: string }[];
  layout: TemplateTabData[];
  views: { id: string; name: string }[];
  mandatory_completion_score: number;
  completion_score: number;
  show_total_completion_score?: boolean;
  show_mandatory_completion_score?: boolean;
};

export type EntityProfile = EntityProfileSummary & {
  schema: AttributeTemplateSchema;
  groups: AttributeGroupSummary[];
  collections: CollectionSchema[];
  staged_entity_id: string | null;
  mandatory_completion_score: number;
  display_completion_score: number;
  completion_score: number;
  cover_image: Assets | null;
  status: EntityStatus;
  display_mandatory_completion_score: number;
  attribute_completion_values: {
    filled_attribute_count: number;
    filled_mandatory_attribute_count: number;
    total_mandatory_attribute_count: number;
    total_attribute_count: number;
  };
};

export type EntityProfilePaginatedOutput = {
  pagination: {
    total: number;
    offset: number;
    limit: number;
  };
  data: EntityProfileSummary[];
};

/**
 * @see server/src/agilis/models/odm.py EntityProfileExportResponseSchema
 */
export type EntityExportStatusSchema = {
  id: UUID;
  number: string;
  query_parameters: Record<string, number | string | Array<unknown>>;
  total_entities: number;
  status: ProductUploadStatus;
  document: ProductSelectorCSVDocument | null;
};

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  customer: "Customer",
  supplier: "Supplier",
  distributor: "Distributor",
};

export const ENTITY_MODIFY_PERMISSIONS: Record<EntityType, Permission> = {
  customer: "modify_customers",
  supplier: "modify_suppliers",
  distributor: "modify_distributors",
};

export const ENTITY_STATUS_LABELS: Record<EntityStatus, string> = {
  draft: "Draft",
  published: "Published",
  staged: "Staged",
  unpublished: "Unpublished",
  unpublished_staged: "Unpublished Staged",
  archived: "Archived",
};

export type EntityHierarchySummarySchema = {
  id: UUID;
  name: string;
  entity_type: EntityType;
  status: EntityStatus;
  attributes: { name: string; display_name: string; value: string }[];
};

export type SubsidiaryCandidate = {
  id: UUID;
  name: string;
  entity_type: EntityType;
  status: EntityStatus;
  attributes: { name: string; display_name: string; value: string }[];
};

export type SubsidiaryCandidatesResponse = {
  pagination: {
    total: number;
    offset: number;
    limit: number;
  };
  data: SubsidiaryCandidate[];
  subsidiary_preview_attributes: { display_name: string; name: string }[];
};

export type EntityHierarchyResponse = {
  parent: EntityHierarchySummarySchema | null;
  subsidiaries: EntityHierarchySummarySchema[];
  is_parent: boolean;
  parent_preview_attributes: { display_name: string; name: string }[];
  is_subsidiary: boolean;
  subsidiaries_count: number;
  attribute_definitions: { display_name: string; name: string }[];
  subsidiary_preview_attributes: { display_name: string; name: string }[];
};

export type RelatedEntityProfile = {
  id: UUID;
  name: string;
  object_type: EntityType | "product";
  object_number: string | null;
  status: EntityStatus | "active" | "inactive" | null;
  preview_attributes?: { key: string; value: string }[];
};

export type RelatedEntitiesGroup = {
  target_side_display_name: string;
  attribute_id: UUID;
  source_object_type: EntityType | "product" | null;
  source_preview_attributes?: {
    id: string;
    name: string | null;
    display_name: string | null;
    input_type: string | null;
  }[];
  source_profiles: RelatedEntityProfile[];
  total_count: number;
};

export type RelatedEntitiesResponse = {
  groups: RelatedEntitiesGroup[];
  has_related_entities: boolean;
};

export type ObjectReferenceArchiveImpact = {
  reference_count: number;
  referencing_profile_count: number;
};

export type SubsidiaryTableRow = {
  id: UUID;
  name: string;
  status: EntityStatus;
  attributes: { name: string; display_name: string; value: string }[];
};

export type BulkUploadCustomerType = "customers" | "distributors";
